// STEP MOA-LIVE-VOICE-OVERLAY-1 · 라모 음성 → 기존 오버레이 제어 (기존 ops·상태 REUSE · 판매자 기본값 저장 X).
import { displayMode, combinedItems } from '@/lib/overlayLayout';
import { sizeTableHasData } from '@/components/live/OverlayExtras';

const PRESETS = ['default', 'full', 'price', 'options', 'mini'];
const PRESET_LABEL = { default: '기본형', full: '전체정보형', price: '가격형', options: '옵션형', mini: '미니형' };
const WITH_PRICE = ['default', 'full', 'price', 'mini'];
const WITH_OPTIONS = ['options', 'full'];
const OVERLAY_INTENTS = ['overlay.preset', 'overlay.field', 'overlay.qr', 'overlay.size_table', 'overlay.mode',
  'overlay.combined', 'overlay.orientation', 'past.show', 'past.hide'];

const ok = (text) => ({ ok: true, text });
const fail = (text) => ({ ok: false, text });
const qrSec = (d) => (d === 'persistent' || d === 0 ? 0 : Number(d) || 10);

export const isOverlayIntent = (intent) => OVERLAY_INTENTS.includes(intent);

export const runOverlayIntent = async (intent, params, c) => {
  const { activePid, active, display, isTestMode, ops, testPatch, overlay } = c;
  const shown = !!activePid && display.mode !== 'hidden';
  const cur = display.preset && display.preset !== 'hidden' ? display.preset : 'default';
  const combined = displayMode(overlay?.layout) === 'combined';
  const items = combinedItems(overlay?.layout);
  const session = (patch) => overlay?.setSessionTop?.(patch);

  const applyPreset = async (p) => {
    if (isTestMode) testPatch((x) => ({ ...x, active_product_display: { ...(x.active_product_display || {}), preset: p, mode: 'visible' } }));
    else await ops.applyPreset(activePid, p);
  };
  const hideProduct = async () => {
    if (isTestMode) testPatch((x) => ({ ...x, active_product_display: { ...(x.active_product_display || {}), mode: 'hidden', size_table_visible: false } }));
    else await ops.hideOverlay(activePid);
  };
  const needProduct = () => (!activePid ? fail('표시 중인 상품이 없어요') : null);

  if (intent === 'past.show' || intent === 'past.hide') {
    const past = overlay?.past;
    if (!past) return fail('지난 상품 제어를 사용할 수 없어요');
    if (intent === 'past.hide') {
      if (!past.playing) return fail('지난 상품이 표시 중이 아니에요');
      past.stop();
      return ok('지난 상품 숨김 완료');
    }
    if (!past.count) return fail('지난 상품이 없어요');
    if (past.mode === 'off') return fail('지난 상품 설정이 꺼져 있어요 (설정에서 1회·반복 선택)');
    past.start();
    return ok('지난 상품 표시 시작 (미리보기)');
  }

  if (intent === 'overlay.mode') {
    const m = params?.mode === 'combined' ? 'combined' : 'separate';
    session({ display_mode: m });
    return ok(m === 'combined' ? '통합 카드로 전환 완료' : '개별 표시로 전환 완료');
  }

  if (intent === 'overlay.orientation') {
    if (!combined) return fail('카드 형태 변경은 통합 카드에서만 음성으로 할 수 있어요');
    const o = params?.orientation === 'vertical' ? 'vertical' : 'horizontal';
    session({ combined_orientation: o });
    return ok(o === 'vertical' ? '통합 카드 세로형 변경 완료' : '통합 카드 가로형 변경 완료');
  }

  const np = needProduct();
  if (np) return np;

  if (intent === 'overlay.combined') {
    if (params?.action === 'hide') {
      if (!combined || !shown) return fail('통합 카드가 표시 중이 아니에요');
      await hideProduct();
      return ok('통합 카드 숨김 완료');
    }
    session({ display_mode: 'combined' });
    if (!shown) await applyPreset(cur);
    return ok('통합 카드 표시 완료');
  }

  if (intent === 'overlay.preset') {
    const p = params?.preset === 'original' ? (PRESETS.includes(overlay?.draftPreset) ? overlay.draftPreset : 'default') : params?.preset;
    if (!PRESETS.includes(p)) return fail('해당 프리셋이 없어요');
    await applyPreset(p);
    return ok(`${PRESET_LABEL[p]} 적용 완료${params?.preset === 'original' ? ' (기본 설정)' : ''}`);
  }

  if (intent === 'overlay.field') {
    const { field, action } = params || {};
    if (field === 'image' || field === 'name') return fail('상품 사진·상품명만 따로 켜고 끄는 기능은 아직 없어요');
    if (field === 'price') {
      if (action === 'hide') return fail('가격만 숨기는 표시 형태가 없어 실행하지 않았어요');
      if (action === 'only') { await applyPreset('price'); return ok('가격형 적용 완료'); }
      if (shown && WITH_PRICE.includes(cur)) return ok('가격이 이미 표시 중이에요');
      await applyPreset('full');
      return ok('가격 추가 표시 완료 (전체정보형)');
    }
    if (field === 'options') {
      if (action === 'only') { await applyPreset('options'); return ok('옵션형 적용 완료'); }
      if (action === 'hide') {
        if (!WITH_OPTIONS.includes(cur)) return fail('옵션은 이미 표시되지 않아요');
        await applyPreset('default');
        return ok('옵션 숨김 완료 (기본형)');
      }
      if (shown && WITH_OPTIONS.includes(cur)) return ok('옵션이 이미 표시 중이에요');
      await applyPreset('full');
      return ok('옵션 추가 표시 완료 (전체정보형)');
    }
    return fail('어떤 항목인지 확실하지 않아 실행하지 않았어요');
  }

  if (!shown) return fail('상품 카드가 내려져 있어요');
  const show = params?.action !== 'hide';

  if (intent === 'overlay.size_table') {
    if (show && !sizeTableHasData(active?.size_table)) return fail('이 상품은 상품설명이 없어요');
    if (combined) {
      session({ combined_items: { ...items, size_table: show } });
      return ok(show ? '통합 카드 상품설명 포함 완료' : '통합 카드 상품설명 제외 완료');
    }
    if (isTestMode) testPatch((x) => ({ ...x, active_product_display: { ...(x.active_product_display || {}), size_table_visible: show } }));
    else await ops.toggleSizeTable(activePid, cur, show);
    return ok(show ? '상품설명 표시 완료' : '상품설명 숨김 완료');
  }

  if (intent === 'overlay.qr') {
    if (combined) {
      session({ combined_items: { ...items, qr: show } });
      return ok(show ? `통합 카드 QR 포함 완료${params?.duration != null ? ' (표시 시간은 개별 표시에서만 적용)' : ''}` : '통합 카드 QR 제외 완료');
    }
    if (!show) {
      if (isTestMode) testPatch((x) => ({ ...x, active_product_qr: { visible: false } }));
      else await ops.hideQr(activePid);
      return ok('QR 숨김 완료');
    }
    const sec = qrSec(params?.duration != null ? params.duration : overlay?.qrDefault);
    if (isTestMode) {
      testPatch((x) => ({ ...x, active_product_qr: { visible: true, expires_at: sec ? new Date(Date.now() + sec * 1000).toISOString() : null } }));
    } else await ops.showQr(activePid, sec);
    return ok(`QR ${sec ? `${sec}초` : '계속'} 표시 완료${isTestMode ? ' (테스트 샘플 QR)' : ''}`);
  }
  return null;
};
